import { DownOutlined } from '@ant-design/icons'
import type { ApolloClient } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { Button, Dropdown, Modal } from 'antd'
import dayjs from 'dayjs'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import {
  CloneSourceDocument,
  type CloneSourceMutation,
  SourceStage,
  UpdateSourceStageDocument,
} from '~app/graph/generated/graphql'
import i18n from '~app/i18n/i18n'
import { useUpdatingContext } from '../useUpdatingContext'
import { useSourceDate } from './StartDate'

/*
 * Hooks
 */
const useCloneSource = (
  sourceId: string,
  onDone: VoidFunction
): (() => Promise<ApolloClient.MutateResult<CloneSourceMutation>>) => {
  const [cloneSourceMutation] = useMutation(CloneSourceDocument)
  const { setUpdating } = useUpdatingContext()
  return () => {
    setUpdating(sourceId, true)
    return cloneSourceMutation({
      variables: {
        id: sourceId,
      },
    })
      .then(result => {
        onDone()
        return result
      })
      .finally(() => setUpdating(sourceId, false))
  }
}

const useSourceStage = (
  sourceId: string,
  startAt: string,
  onDone: VoidFunction
): {
  loading: boolean
  updateSourceStage: (newStage: SourceStage) => Promise<void>
} => {
  const { setUpdating } = useUpdatingContext()
  const [updateSourceStageMutation, { loading: sourceStageLoading }] =
    useMutation(UpdateSourceStageDocument)
  const { updateSourceDate, loading: sourceDateLoading } =
    useSourceDate(sourceId)

  return {
    loading: sourceDateLoading || sourceStageLoading,
    updateSourceStage: async (stage: SourceStage) => {
      setUpdating(sourceId, true)
      try {
        await updateSourceStageMutation({ variables: { id: sourceId, stage } })

        // TODO: Why is this not done in the backend ??
        //
        // In the case where the forwarding is being activated manually,
        // we need to autogenerate the startAt and end at dates
        // to the start of today and based on the forwarding duration only if it's not already set
        if (stage === SourceStage.Activated && !startAt) {
          await updateSourceDate(dayjs().startOf('day').toISOString())
        }
        onDone()
      } finally {
        setUpdating(sourceId, false)
      }
    },
  }
}

/*
 * util
 */

const getNextStage = (stage: SourceStage, t: TFunction) =>
  stage === SourceStage.Activating
    ? {
        nextStage: SourceStage.Activated,
        label: t('sources.sourceActions.activate'),
      }
    : stage === SourceStage.Renewing
      ? {
          nextStage: SourceStage.Activating,
          label: t('sources.sourceActions.activating'),
        }
      : stage === SourceStage.Cancelling
        ? {
            nextStage: SourceStage.Canceled,
            label: t('sources.sourceActions.cancel'),
          }
        : {
            nextStage: SourceStage.Activating,
            label: t('sources.sourceActions.request'),
          }

/*
 * Component
 */

interface Props {
  sourceId: string
  stage: SourceStage
  startAt: string
  onDone: VoidFunction
}

const Stage = ({ sourceId, startAt, stage, onDone }: Props) => {
  const { t } = useTranslation()
  const { updating } = useUpdatingContext()
  const { updateSourceStage, loading } = useSourceStage(
    sourceId,
    startAt,
    onDone
  )
  const cloneSource = useCloneSource(sourceId, onDone)

  const { nextStage, label } = getNextStage(stage, t)

  const excludedStages = [
    SourceStage.Expired,
    SourceStage.Cancelling,
    SourceStage.Canceled,
    SourceStage.NoExtension,
  ]

  return (
    <div>
      <div>
        <Dropdown
          disabled={!!updating[sourceId] || loading}
          trigger={['click']}
          menu={{
            items: [
              {
                key: '0',
                label,
                onClick: () => updateSourceStage(nextStage),
              },
              {
                key: '1',
                label: `${i18n.t('labels.clone')}${stage === SourceStage.NoExtension ? ' ⚠️' : ''}`,
                onClick: () => {
                  if (stage === SourceStage.NoExtension) {
                    Modal.confirm({
                      title: i18n.t('sources.confirmationModal.title'),
                      content: i18n.t('sources.confirmationModal.body'),
                      cancelText: i18n.t('labels.cancel'),
                      okText: i18n.t('labels.yes'),
                      onOk: () => cloneSource(),
                      onCancel() {
                        return
                      },
                    })
                  } else {
                    cloneSource()
                  }
                },
              },
              {
                key: '2',
                label: i18n.t('labels.cancel'),
                disabled: excludedStages.includes(stage),
                onClick: () => updateSourceStage(SourceStage.Cancelling),
              },
              {
                key: '3',
                label: i18n.t('labels.noExtension'),
                disabled: excludedStages.includes(stage),
                onClick: () => updateSourceStage(SourceStage.NoExtension),
              },
            ],
          }}
        >
          <Button>
            {i18n.t(`sources.sourceStages.${stage}`)}
            <DownOutlined />
          </Button>
        </Dropdown>
      </div>
    </div>
  )
}
export default Stage
